import React from 'react';
import { Plus, Trash2 } from 'react-feather';
import { HoverOver } from '../../../../../../../../GlobalComponents/Builders';
import FlexInputBuilder from '../../../../../../../../GlobalComponents/Builders/FlexInputBuilder';

/**
 * DMTJsonFieldSearch (REQ-365)
 *
 * Presentational panel for the "JSON Field Search" toggle area inside the DMT
 * progress dashboard search form. All state and helpers are supplied via props;
 * this component holds no state and performs no API/search logic.
 *
 * Inputs are rendered through FlexInputBuilder so they inherit the shared SCSS
 * form styling (input-{sm|md} sizing, react-select theming) instead of raw
 * <input>/<select> elements.
 */
const DMTJsonFieldSearch = ({
    jobResources,
    showMultiSearch,
    operators,
    columnIsJson,
    opIsNoValue,
    opIsList,
    columnOptions,
    filterRows,
    setFilterRows,
    match,
    setMatch,
    mode,
    setMode,
    resource,
    setResource,
    groupBy,
    setGroupBy,
    countResult,
    isResult,
    emptyFilter,
}) => {

    const resourceOptions = [
        { label: 'All Resources', value: '' },
        ...Object.values(jobResources)
            .filter(r => r.filter !== 'All' && r.resource_id != null)
            .map(r => ({ label: r.resource, value: String(r.resource_id) })),
    ];

    const matchOptions = [
        { label: 'ALL (AND)', value: 'and' },
        { label: 'ANY (OR)', value: 'or' },
    ];

    const modeOptions = [
        { label: 'Record List', value: 'list' },
        { label: 'Count / Aggregate', value: 'count' },
    ];

    const groupByOptions = [{ label: '— none —', value: '' }, ...columnOptions];

    return (
        <div style={{ marginTop: showMultiSearch ? 12 : 0 }}>
            <p style={{ fontSize: 11, color: '#999', marginBottom: 8 }}>
                Filter records by JSON field values (via JEXTRACT) or plain columns. Combine filters with AND/OR. Use an array-element condition to match values inside array fields (e.g. types, customFields, communicationItems).
            </p>

            {/* Match connector + output mode + resource scope */}
            <div className='d-flex align-items-end flex-wrap gap-3 mb-3'>
                <FlexInputBuilder layout='inline' inputSize='sm' inputs={[
                    { name: 'jsonMatch', label: 'Match', type: 'reactselect', value: match, onChange: (v) => setMatch(v || 'and'), options: matchOptions, isSearchable: false, width: 130 },
                    { name: 'jsonMode', label: 'Mode', type: 'reactselect', value: mode, onChange: (v) => setMode(v || 'list'), options: modeOptions, isSearchable: false, width: 210 },
                    { name: 'jsonResource', label: 'Resource', type: 'reactselect', value: resource, onChange: (v) => setResource(v || ''), options: resourceOptions, placeholder: 'All Resources', width: 280 },
                ]} />
            </div>

            {/* Filter rows */}
            {filterRows.map((row, idx) => {
                const isJsonCol = columnIsJson(row.column);
                const noValue = opIsNoValue(row.op);
                const isList = opIsList(row.op);
                const updateRow = (patch) => setFilterRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));

                // Operators available for this row (arrayAny excludes list/no-value ops).
                const rowOperatorOptions = operators
                    .filter(o => !(row.arrayAny && (o.listValue || o.noValue)))
                    .map(o => ({ label: o.label, value: o.value }));

                // ── Array-element condition: full-width stacked card ──
                if (row.elementMatch) {
                    const condOperatorOptions = operators.filter(o => !o.listValue).map(o => ({ label: o.label, value: o.value }));
                    return (
                        <div key={idx} style={{ borderLeft: '2px solid var(--theme-default)', paddingLeft: 10, marginBottom: 10 }}>
                            {/* Header: column + array field, delete row on the right */}
                            <div className='d-flex align-items-end justify-content-between gap-2 mb-2'>
                                <FlexInputBuilder layout='inline' inputSize='md' inputs={[
                                    { name: `col-${idx}`, label: 'Column', type: 'reactselect', value: row.column, onChange: (v) => updateRow({ column: v || 'resource_data' }), options: columnOptions, isSearchable: false, width: 190 },
                                    { name: `arrpath-${idx}`, label: 'Array field', type: 'text', value: row.elementMatch.arrayPath, onChange: (v) => updateRow({ elementMatch: { ...row.elementMatch, arrayPath: v } }), placeholder: 'e.g. customFields', width: 240 },
                                ]} />
                                {filterRows.length > 1 && (
                                    <Trash2 size={16} className='icon-solid-danger' style={{ cursor: 'pointer', marginBottom: 6 }} onClick={() => setFilterRows(prev => prev.filter((_, i) => i !== idx))} />
                                )}
                            </div>

                            {/* Conditions: match an element where ALL of these hold */}
                            <div style={{ fontSize: 11, color: '#999', marginBottom: 6 }}>Match an element where ALL of:</div>
                            {row.elementMatch.conditions.map((cond, cIdx) => {
                                const updateCond = (patch) => updateRow({ elementMatch: { ...row.elementMatch, conditions: row.elementMatch.conditions.map((c, ci) => ci === cIdx ? { ...c, ...patch } : c) } });
                                const condNoValue = opIsNoValue(cond.op);
                                return (
                                    <div key={cIdx} className='d-flex align-items-end gap-2 mb-2'>
                                        <FlexInputBuilder layout='inline' inputSize='sm' className='flex-grow-1' inputs={[
                                            { name: `ck-${idx}-${cIdx}`, type: 'text', value: cond.key, onChange: (v) => updateCond({ key: v }), placeholder: 'key (e.g. caption)', width: 200 },
                                            { name: `co-${idx}-${cIdx}`, type: 'reactselect', value: cond.op, onChange: (v) => updateCond({ op: v }), options: condOperatorOptions, isSearchable: false, width: 130 },
                                            { name: `cv-${idx}-${cIdx}`, type: 'text', value: cond.value, onChange: (v) => updateCond({ value: v }), placeholder: 'value', flex: '1 1 auto', visible: !condNoValue },
                                        ]} />
                                        <span style={{ width: 16, display: 'inline-flex', justifyContent: 'center', marginBottom: 6 }}>
                                            {row.elementMatch.conditions.length > 1 && (
                                                <Trash2 size={14} className='icon-solid-danger' style={{ cursor: 'pointer' }} onClick={() => updateRow({ elementMatch: { ...row.elementMatch, conditions: row.elementMatch.conditions.filter((_, ci) => ci !== cIdx) } })} />
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                            <span style={{ cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }} className='txt-primary'
                                onClick={() => updateRow({ elementMatch: { ...row.elementMatch, conditions: [...row.elementMatch.conditions, { key: '', op: '=', value: '' }] } })}>
                                <Plus size={12} /> Add condition
                            </span>
                        </div>
                    );
                }

                // ── Standard scalar / JEXTRACT / array-any leaf filter (single inline row) ──
                return (
                    <div key={idx} style={{ borderLeft: '2px solid transparent', paddingLeft: 10, marginBottom: 8 }}>
                        <div className='d-flex align-items-end gap-2'>
                            {/* Column */}
                            <FlexInputBuilder layout='inline' inputSize='md' inputs={[
                                { name: `col-${idx}`, type: 'reactselect', value: row.column, onChange: (v) => updateRow({ column: v || 'resource_data', path: '' }), options: columnOptions, isSearchable: false, width: 190 },
                            ]} />

                            {/* JSON path (only for JSON columns) */}
                            {isJsonCol && (
                                <FlexInputBuilder layout='inline' inputSize='md' className='flex-grow-1' style={{ flex: row.arrayAny ? '1' : '0 0 200px' }} inputs={[
                                    { name: `path-${idx}`, type: 'text', value: row.path, onChange: (v) => updateRow({ path: v }), placeholder: row.arrayAny ? 'arrayKey/leafKey (e.g. types/name)' : 'JSON path (e.g. company/id)', flex: '1 1 auto' },
                                ]} />
                            )}
                            {/* Operator */}
                            <FlexInputBuilder layout='inline' inputSize='md' inputs={[
                                { name: `op-${idx}`, type: 'reactselect', value: row.op, onChange: (v) => updateRow({ op: v }), options: rowOperatorOptions, isSearchable: false, width: 140 },
                            ]} />
                            {/* Value */}
                            {!noValue && (
                                <FlexInputBuilder layout='inline' inputSize='md' className='flex-grow-1' inputs={[
                                    { name: `val-${idx}`, type: 'text', value: row.value, onChange: (v) => updateRow({ value: v }), placeholder: isList ? 'comma,separated,values' : 'value', flex: '1 1 auto' },
                                ]} />
                            )}

                            {/* Row controls */}
                            <div className='d-flex align-items-center gap-3' style={{ paddingBottom: 6 }}>
                                {isJsonCol && (
                                    <HoverOver
                                        content='Match any element inside an array field. Use an arrayKey/leafKey path (e.g. types/name).'
                                        text={
                                            <label className='d-flex align-items-center' style={{ gap: 6, fontSize: 13, fontWeight: 400, cursor: 'pointer', margin: 0, whiteSpace: 'nowrap' }}>
                                                <input
                                                    type='checkbox'
                                                    className='form-check-input'
                                                    style={{ margin: 0 }}
                                                    checked={!!row.arrayAny}
                                                    onChange={() => updateRow({ arrayAny: !row.arrayAny })}
                                                />
                                                Array match
                                            </label>
                                        }
                                    />
                                )}
                                {filterRows.length > 1 && (
                                    <Trash2 size={16} className='icon-solid-danger' style={{ cursor: 'pointer' }} onClick={() => setFilterRows(prev => prev.filter((_, i) => i !== idx))} />
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}

            <div className='d-flex align-items-center gap-3' style={{ marginTop: 4 }}>
                <span style={{ cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }} className='txt-primary'
                    onClick={() => setFilterRows(prev => [...prev, emptyFilter()])}>
                    <Plus size={14} /> Add Filter
                </span>
                <span style={{ cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }} className='txt-primary'
                    onClick={() => setFilterRows(prev => [...prev, { column: 'resource_data', elementMatch: { arrayPath: '', conditions: [{ key: '', op: '=', value: '' }] } }])}>
                    <Plus size={14} /> Add Array-Element Condition
                </span>
                {filterRows.length > 1 && (
                    <span style={{ cursor: 'pointer', fontSize: 12 }} className='txt-secondary' onClick={() => setFilterRows([emptyFilter()])}>Clear All</span>
                )}
            </div>

            {/* Count-mode group-by */}
            {mode === 'count' && (
                <div className='d-flex align-items-end gap-2 mt-3' style={{ borderTop: '1px dashed #eee', paddingTop: 12 }}>
                    <FlexInputBuilder layout='inline' inputSize='sm' inputs={[
                        { name: 'jsonGroupBy', label: 'Group by (optional)', type: 'reactselect', value: groupBy.column, onChange: (v) => setGroupBy({ column: v || '', path: '' }), options: groupByOptions, isSearchable: false, width: 200 },
                        ...(groupBy.column && columnIsJson(groupBy.column) ? [
                            { name: 'jsonGroupByPath', label: 'Path', type: 'text', value: groupBy.path, onChange: (v) => setGroupBy(prev => ({ ...prev, path: v })), placeholder: 'JSON path (e.g. status/name)', width: 220 },
                        ] : []),
                    ]} />
                </div>
            )}

            {/* Count-mode result */}
            {isResult && countResult && (
                <div className='mt-3' style={{ background: '#f8f9fa', borderRadius: 6, padding: '12px 16px' }}>
                    <div style={{ fontSize: 13 }}>Total matching records: <strong>{(countResult.total ?? 0).toLocaleString()}</strong></div>
                    {countResult.grouped && countResult.groups && countResult.groups.length > 0 && (
                        <table className='table table-sm mt-2' style={{ fontSize: 12, marginBottom: 0 }}>
                            <thead><tr><th>Value</th><th style={{ textAlign: 'right' }}>Count</th></tr></thead>
                            <tbody>
                                {countResult.groups.map((g, gi) => (
                                    <tr key={gi}><td>{g.value === null || g.value === '' ? <em style={{ color: '#999' }}>(empty)</em> : String(g.value)}</td><td style={{ textAlign: 'right' }}>{g.count.toLocaleString()}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};

export default DMTJsonFieldSearch;
